import { CornerDownRight } from 'lucide-react'

import { SheetSectionCard } from '@/components/SheetSectionCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { OutOfDateBadge, VariantsHelp } from './OrgTooltips'
import { DiffRowButton } from './SnippetVariantsTab'
import type { SnippetOrgUsage } from './types'
import { orgDisplayName, selectionKey, type OrgRow } from './utils'

interface SnippetUsageTabProps {
  /** The other orgs, in display order. Null while loading. */
  orgs: SnippetOrgUsage[] | null
  error: string | null
  onRetry: () => void
  onOpen: (row: OrgRow) => void
}

/**
 * The organizations that read a shared snippet. A customized one opens its
 * wording against Careforce's, and each of its variants against that wording.
 */
export function SnippetUsageTab(props: SnippetUsageTabProps) {
  return (
    <SheetSectionCard
      title={
        props.orgs
          ? `Organizations using it · ${props.orgs.length}`
          : 'Organizations using it'
      }
      body={<UsageBody {...props} />}
    />
  )
}

function UsageBody({ orgs, error, onRetry, onOpen }: SnippetUsageTabProps) {
  if (error) {
    return (
      <div className="space-y-2 text-sm">
        <p className="text-destructive">{error}</p>
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      </div>
    )
  }

  if (!orgs) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    )
  }

  if (!orgs.length) {
    return (
      <p className="text-sm text-muted-foreground">
        No other organization uses this snippet yet.
      </p>
    )
  }

  return (
    <Table containerClassName="overflow-hidden rounded-lg border border-neutral-subtle-border">
      <TableHeader>
        <TableRow>
          <TableHead>Organization</TableHead>
          <TableHead className="w-20">Duties</TableHead>
          <TableHead className="w-28">
            <span className="flex items-center gap-1">
              Variants
              <VariantsHelp />
            </span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {orgs.map((org) => (
          <OrgRows key={org.org_slug} org={org} onOpen={onOpen} />
        ))}
      </TableBody>
    </Table>
  )
}

function OrgRows({
  org,
  onOpen,
}: Pick<SnippetUsageTabProps, 'onOpen'> & { org: SnippetOrgUsage }) {
  const name = orgDisplayName(org)

  if (!org.copy) {
    return (
      <TableRow>
        <TableCell>{name}</TableCell>
        <TableCell className="text-muted-foreground">
          {org.duty_count}
        </TableCell>
        <TableCell
          aria-label="Cannot have variants"
          className="text-muted-foreground">
          –
        </TableCell>
      </TableRow>
    )
  }

  const row: OrgRow = { kind: 'orgCopy', orgSlug: org.org_slug }
  return (
    <>
      <TableRow className="relative">
        <TableCell>
          <DiffRowButton
            rowKey={selectionKey(row)}
            onOpen={() => onOpen(row)}
            badges={
              <>
                <Badge variant="success">Customized</Badge>
                {org.copy.out_of_date && <OutOfDateBadge orgName={name} />}
              </>
            }>
            {name}
          </DiffRowButton>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {org.duty_count}
        </TableCell>
        <TableCell className="text-muted-foreground">
          {org.copy.variants.length}
        </TableCell>
      </TableRow>
      {org.copy.variants.map((variant) => {
        const variantRow: OrgRow = {
          kind: 'orgVariant',
          orgSlug: org.org_slug,
          dutyId: variant.duty_id,
        }
        return (
          <TableRow key={variant.duty_id} className="relative">
            <TableCell className="pl-6">
              <DiffRowButton
                rowKey={selectionKey(variantRow)}
                onOpen={() => onOpen(variantRow)}>
                <CornerDownRight
                  aria-hidden="true"
                  className="mr-1.5 inline size-3.5 text-neutral-low-contrast"
                />
                {variant.duty_name}
              </DiffRowButton>
            </TableCell>
            <TableCell />
            <TableCell />
          </TableRow>
        )
      })}
    </>
  )
}
